/**
 * Per-page DiSC chapter blueprints.
 *
 * Every key marketing page imports its chapter set from here and feeds it
 * into <DiscChapters chapters={...} />. This file is the single source
 * of truth for the DiSC voice across the entire site.
 *
 * Each chapter shape:
 *   { disc: "D"|"i"|"S"|"C",
 *     eyebrow: "...",
 *     title: "...",
 *     body: "...",
 *     bullets: ["...", "...", "..."],
 *     stat: { value, suffix?, label, compare?, source? },
 *     cta: { label, action: "demo" | "route", to?: "/path" },
 *     secondary?: { label, to } }
 */

/* ───────────────────── HOME ───────────────────── */
export const HOME_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Scale the clinic. Not the payroll.",
    body: "Your clinic is growing faster than your front desk can keep up. Clinic OS gives you the equivalent of 2-3 extra staff — without the hiring cycle, the training, or the salary line.",
    bullets: [
      "AI Receptionist answers 24/7 — even after-hours",
      "Sales Autopilot re-engages stale leads on its own",
      "Live owner dashboard — calls, bookings, revenue in one view",
    ],
    stat: { value: "+24%", label: "Lead → booking conversion across pilot clinics.", compare: "Industry average: 9%", source: "Clinic OS pilots, 2025" },
    cta: { label: "Show me the growth math", action: "demo" },
    secondary: { label: "See AI Workforce", to: "/ai-workforce" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Make every patient feel known.",
    body: "When a patient calls and gets voicemail, they call a competitor. When their review goes unanswered for two weeks, they tell friends. Clinic OS makes sure every touchpoint feels personal — even when you're with a patient.",
    bullets: [
      "Missed-call auto-SMS converts 41% to bookings",
      "Review AI replies in your tone, on every platform",
      "Reactivation campaigns wake up dormant patients",
    ],
    stat: { value: "98%", label: "Of patient reviews replied to — across Google, Facebook, and Yelp.", compare: "Industry average: 23%", source: "Clinic OS pilots" },
    cta: { label: "Meet KAI", action: "route", to: "/kai" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "Twelve tools. One calm dashboard.",
    body: "Your team is tired. They jump between Jane for charts, Mailchimp for emails, RingCentral for calls, a spreadsheet for follow-ups, Google Reviews for replies. Clinic OS replaces all of it with one screen — and we migrate it for you.",
    bullets: [
      "Unified inbox — calls, SMS, email, social, reviews",
      "Onboarding done with you, not handed off",
      "Migration assistance + free team training",
    ],
    stat: { value: "11 hrs", label: "Back to your team every week, every clinic.", compare: "Before Clinic OS: spent on tool-juggling", source: "Average across 22+ clinics" },
    cta: { label: "Show me one calm system", action: "route", to: "/welcome#path-calm" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Numbers that finally add up.",
    body: "You don't trust dashboards that ignore your EMR. Clinic OS connects 2-way to Jane, Juvonno, ClinicSense and more — so the revenue you see in Clinic Pulse matches the cents you bill. Audit-grade, HIPAA-ready, SOC 2 aligned.",
    bullets: [
      "Live Clinic Pulse — revenue, no-shows, recalls",
      "2-way EMR sync — no double entry, no broken availability",
      "HIPAA-ready · SOC 2 aligned · 99.9% uptime",
    ],
    stat: { value: "live", label: "Time-to-revenue visibility. Compared to weekly EMR exports.", source: "Clinic Pulse" },
    cta: { label: "Show me the numbers", action: "route", to: "/juvonno/clinicpulse" },
  },
];

/* ───────────────────── PLATFORM ───────────────────── */
export const PLATFORM_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Modules that move the needle.",
    body: "Every Clinic OS module ships with the outcome it's responsible for. No vanity features. If a module doesn't move a number on your owner dashboard, it doesn't get a tile.",
    bullets: [
      "AI Workforce → +24% lead-to-booking",
      "Sales Autopilot → $8.4k avg monthly revenue recovery",
      "Call IQ → 97% call answer rate, after-hours included",
    ],
    stat: { value: "8", suffix: "core modules", label: "Each module owns a measurable outcome on your dashboard.", source: "Clinic OS Platform" },
    cta: { label: "See the modules", action: "route", to: "/products" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "A clinic that talks back.",
    body: "Patients reach out from six different places. KAI listens to all of them, responds in your tone, escalates only when a human is needed.",
    bullets: [
      "Unified inbox across calls, SMS, email, DMs, reviews",
      "Tone-matched replies — not robotic chatbot lines",
      "Smart escalation — humans see only what needs them",
    ],
    stat: { value: "< 60s", label: "Average first-touch reply time across every channel.", compare: "Industry average: ~8 hrs", source: "KAI" },
    cta: { label: "Meet KAI", action: "route", to: "/kai" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "Same OS. Every specialty.",
    body: "Physiotherapy, dental, dietetics, chiropractic, med-spa, golf-club wellness — same platform, tuned templates per specialty. No re-platforming when you add a new service line.",
    bullets: [
      "6 specialty templates ship configured out-of-the-box",
      "Add specialties without changing your OS",
      "Templates evolve with your team — not your IT roadmap",
    ],
    stat: { value: "14 days", label: "Average go-live across all specialties.", source: "Onboarding playbook" },
    cta: { label: "Find my specialty", action: "route", to: "/welcome#path-new" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Every workflow, accountable.",
    body: "Open architecture. Audit log on every action. 2-way EMR sync, no data lock-in. The platform you can hand to compliance without flinching.",
    bullets: [
      "Audit log on every action (AI included)",
      "2-way sync — your data, your EMR, always",
      "HIPAA-ready · SOC 2 aligned · 99.9% uptime",
    ],
    stat: { value: "100%", label: "Of platform actions captured in the audit log.", source: "Compliance pack" },
    cta: { label: "See integrations & security", action: "route", to: "/integrations" },
  },
];

/* ───────────────────── KAI ───────────────────── */
export const KAI_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Calls turning into bookings.",
    body: "Every missed call is revenue walking out. The AI Receptionist answers in under 3 rings, qualifies the lead, books the appointment, and updates your EMR — 24/7.",
    bullets: [
      "97% call answer rate — after-hours included",
      "Books straight into Jane, Juvonno or ClinicSense",
      "Hand-off to humans for VIP or complex cases only",
    ],
    stat: { value: "97%", label: "Of inbound calls answered.", compare: "Industry average: 62%", source: "AI Receptionist" },
    cta: { label: "Show me the AI Receptionist", action: "demo" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Conversations that close themselves.",
    body: "Copilot drafts every text, email and social reply in your clinic's voice — your team approves with one tap. The patient never feels like they're talking to a bot, because they aren't.",
    bullets: [
      "Drafts every reply across SMS, email, DM, review",
      "Tone trained on your existing 100 best replies",
      "Approve-with-one-tap or fully automated, your call",
    ],
    stat: { value: "98%", label: "Reviews replied to across Google + Facebook.", compare: "Industry: 23%", source: "Review AI" },
    cta: { label: "Show me happier patients", action: "demo" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "One voice across every channel.",
    body: "Your front desk says one thing. Your evening team says another. Your texts read like a different clinic. KAI gives every channel the same warm, consistent voice — even when you switch staff.",
    bullets: [
      "SOPs encoded into KAI's tone and answers",
      "Stays consistent through staff turnover",
      "Ask Mathew — your SOP, but a chatbot for the team",
    ],
    stat: { value: "1", suffix: "voice", label: "Across calls, texts, emails, DMs, reviews and front-desk hand-offs.", source: "KAI tone model" },
    cta: { label: "See Ask Mathew", action: "route", to: "/kai-experience" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Every conversation, measurable.",
    body: "Call IQ records, transcribes and scores every patient conversation. Spot trends, coach reps, prove ROI without listening to every call.",
    bullets: [
      "Recording + transcription on every call",
      "Sentiment + intent classification",
      "Coaching cues for front-desk training",
    ],
    stat: { value: "100%", label: "Of patient calls recorded, transcribed and scored.", source: "Call IQ AI" },
    cta: { label: "See Call IQ", action: "route", to: "/products/call-iq-ai" },
  },
];

/* ───────────────────── AI WORKFORCE ───────────────────── */
export const AI_WORKFORCE_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Capacity worth 2-3 staff. Zero payroll.",
    body: "Hire AI before you hire a sixth front-desk rep. The AI Workforce answers calls, books appointments, follows up on leads, and runs reactivation — all on autopilot.",
    bullets: [
      "AI Receptionist answers + books 24/7",
      "Sales Autopilot recovers stale leads",
      "Reactivation engine wakes up dormant patients",
    ],
    stat: { value: "$8,400", label: "Average monthly revenue recovered per clinic.", compare: "From no-shows + dormant leads", source: "Pilot data" },
    cta: { label: "Show me the ROI", action: "demo" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "AI that sounds like you.",
    body: "We train every AI agent on your own past conversations, your SOPs, your tone. Patients don't realise they're talking to AI — they realise the clinic feels more attentive than ever.",
    bullets: [
      "Tone learned from your top-100 historical replies",
      "Your scripts, your guardrails, your escalation rules",
      "Patient surveys show no drop in satisfaction — measured.",
    ],
    stat: { value: "+12pts", label: "Average NPS lift in the first 90 days.", source: "Patient surveys post-AI rollout" },
    cta: { label: "Hear it for yourself", action: "route", to: "/kai-experience" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "Calm rollout. 14 days, hands-on.",
    body: "We don't drop AI on your front desk and disappear. We sit with your team for the first two weeks — training, listening to calls together, dialling the AI in.",
    bullets: [
      "Discovery → training → soft-launch → full live in 14 days",
      "Weekly check-ins for the first 60 days",
      "Your team is in the loop on every tone change",
    ],
    stat: { value: "14 days", label: "From kickoff to AI Receptionist taking live calls.", source: "Onboarding playbook" },
    cta: { label: "See onboarding", action: "route", to: "/why-clinicfirst" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Audit-grade. Every AI decision logged.",
    body: "Every booking the AI makes, every reply it sends, every escalation it triggers — captured in the audit log, exportable, attributable. Compliance reviewers love this page.",
    bullets: [
      "Decision-level audit log (input, output, reasoning)",
      "Per-action attribution: AI vs human",
      "HIPAA-ready · SOC 2 aligned",
    ],
    stat: { value: "100%", label: "Of AI actions captured with reasoning + outcome.", source: "AI audit log" },
    cta: { label: "See the audit log", action: "demo" },
  },
];

/* ───────────────────── CLINIC PULSE ───────────────────── */
export const CLINIC_PULSE_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Revenue, live — not weekly.",
    body: "Stop waiting for the Monday EMR export. Pulse shows revenue, bookings, no-shows and recall recovery in real time, by location and by therapist.",
    bullets: [
      "Live revenue stream by location & therapist",
      "No-show + recall recovery dashboards",
      "Owner alerts when a number swings outside band",
    ],
    stat: { value: "live", label: "Revenue visibility, every second of the day.", compare: "vs. weekly EMR exports", source: "Pulse" },
    cta: { label: "Show me the live view", action: "demo" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Sentiment shifts before reviews tank.",
    body: "Pulse blends Call IQ sentiment, review trend lines and front-desk feedback into a single 'patient mood' dial. You see drops in week one — not in next quarter's review average.",
    bullets: [
      "Patient sentiment trend across every channel",
      "Early warnings before review average drops",
      "Coach reps with the exact call transcripts",
    ],
    stat: { value: "7 days", label: "Average lead-time on patient-sentiment dips before they hit Google.", source: "Pulse sentiment model" },
    cta: { label: "See the sentiment view", action: "demo" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "One dashboard. Every location.",
    body: "Whether you're solo or running 10 locations, Pulse rolls everything into one view — and lets you drill down to a single therapist on a single day without leaving the screen.",
    bullets: [
      "Multi-location rollup, single-screen",
      "Drill-down to therapist & day",
      "Compare locations apples-to-apples",
    ],
    stat: { value: "10+", label: "Locations supported in a single rollup view.", source: "Multi-location chain in production" },
    cta: { label: "See the multi-location view", action: "demo" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Audit-grade. Data lineage on every number.",
    body: "Every number on Pulse traces back to its source row in your EMR + the timestamp it was synced. Compliance, finance, insurers — anyone can audit a metric in two clicks.",
    bullets: [
      "Click any number → see source rows + timestamp",
      "Export to CSV, PDF, or BI tool of your choice",
      "Audit log of every dashboard change",
    ],
    stat: { value: "100%", label: "Of metrics traceable to source rows.", source: "Data lineage engine" },
    cta: { label: "See data lineage", action: "demo" },
  },
];

/* ───────────────────── INTEGRATIONS ───────────────────── */
export const INTEGRATIONS_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Plug into the EMR you have.",
    body: "Don't re-platform. Clinic OS plugs into Jane, Juvonno, ClinicSense and more — and adds the layer you wish your EMR had: marketing, AI, calls, reviews.",
    bullets: [
      "Native 2-way sync with major EMRs",
      "No data migration required",
      "Live in under 14 days",
    ],
    stat: { value: "15+", label: "EMR + tool integrations live today.", source: "Integrations directory" },
    cta: { label: "Find my EMR", action: "route", to: "/integrations#flagship-emrs" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Talks to the tools your team loves.",
    body: "Slack, Mailchimp, Twilio, Google Reviews, Meta DMs — every place a patient might reach you, Clinic OS already speaks to. Your team doesn't change workflow; the workflow gets quieter.",
    bullets: [
      "Patient comms tools — Twilio, SMS-Magic, Mailchimp",
      "Review platforms — Google, Facebook, Yelp",
      "Social DMs — Meta, Instagram, WhatsApp",
    ],
    stat: { value: "30+", label: "Comms + social tools integrated.", source: "Integrations directory" },
    cta: { label: "See comms integrations", action: "route", to: "/integrations" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "We migrate. We configure. We train.",
    body: "You don't touch a config screen. Our team plugs Clinic OS into your EMR, migrates your scripts and forms, and trains your team in the first 14 days.",
    bullets: [
      "Hands-on EMR integration setup",
      "Form, script and template migration done for you",
      "Team training included in onboarding",
    ],
    stat: { value: "0", label: "Config screens you'll ever see during setup.", source: "Onboarding playbook" },
    cta: { label: "See onboarding", action: "route", to: "/why-clinicfirst" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "2-way sync. No double entry. No lock-in.",
    body: "Your EMR stays the source of truth. Clinic OS reads + writes back. If you ever leave, your data is yours — clean export, no fees, no drama.",
    bullets: [
      "2-way sync — no shadow records",
      "Open data export — JSON / CSV / API",
      "Field-level mapping you can audit",
    ],
    stat: { value: "100%", label: "Of your data, always yours. No lock-in clauses.", source: "Data policy" },
    cta: { label: "See the data policy", action: "route", to: "/security" },
  },
];

/* ───────────────────── PRODUCTS ───────────────────── */
export const PRODUCTS_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Pick a base. Add what you need.",
    body: "Don't overbuy. Start with Clinic Hub at $297/mo for the essentials, or Clinic Hub+ at $497/mo for growing teams. Bolt on modules only when they make sense for your stage.",
    bullets: [
      "Clinic Hub $297/mo — solo clinic getting organised",
      "Clinic Hub+ $497/mo — growing clinic ready for automations",
      "Add-ons quoted only after a 20-min discovery call",
    ],
    stat: { value: "$297", suffix: "/mo", label: "Where most clinics start. Cancel anytime.", source: "Clinic Hub base" },
    cta: { label: "Compare bases", action: "route", to: "/products" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Modules that talk to each other.",
    body: "Every module shares the same patient record, the same inbox, the same audit log. Stack any 3 add-ons and they behave like they were designed together — because they were.",
    bullets: [
      "Shared patient record across every module",
      "Single inbox — calls, SMS, email, social",
      "AI agents see the full context of every patient",
    ],
    stat: { value: "1", suffix: "record", label: "Per patient. Across every module. Always.", source: "Shared data model" },
    cta: { label: "See the modules", action: "route", to: "/products#add-ons" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "Calm pricing. Cancel anytime.",
    body: "Month-to-month base. Add-ons sized to your clinic, not a sales quota. We tell you when you're paying for something you don't need.",
    bullets: [
      "Month-to-month base — no annual lock-ins",
      "Sizing call before add-ons are quoted",
      "Quarterly review — we right-size if you're over-buying",
    ],
    stat: { value: "0", label: "Hidden fees. No setup, no per-user, no overage gotchas.", source: "Pricing policy" },
    cta: { label: "Book a sizing call", action: "demo" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Line-item transparent.",
    body: "Every add-on shows what's in it, what it integrates with, what it costs, and what outcome it owns. Compare apples-to-apples before you sign anything.",
    bullets: [
      "Line-item breakdown of every add-on",
      "Outcome-per-module published on the page",
      "No SKUs hidden behind contact-us forms",
    ],
    stat: { value: "100%", label: "Of pricing visible on the product page. No 'contact-us'.", source: "Pricing transparency policy" },
    cta: { label: "See all modules", action: "route", to: "/products" },
  },
];

/* ───────────────────── WHY CLINICFIRST ───────────────────── */
export const WHY_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Built by clinic owners who scaled.",
    body: "ClinicFirst was built inside a 10-location physiotherapy chain. We hit every front-desk pain you have. Then we built the OS to fix it — and shipped it to clinics like yours.",
    bullets: [
      "Founded by clinic owners — not SaaS people",
      "Battle-tested across a 10-location chain",
      "Now live in 22+ independent clinics",
    ],
    stat: { value: "10", suffix: "loc chain", label: "The original clinic we built Clinic OS for. Still running on it.", source: "Founding story" },
    cta: { label: "See the story", action: "route", to: "/why-clinicfirst" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "We answer when you call.",
    body: "No ticket-queue runaround. No SLA-trapped support. You text us, you get a human. We sit with your team weekly during onboarding, and every quarter after.",
    bullets: [
      "Slack-channel access during onboarding",
      "Quarterly business reviews — owner to owner",
      "Office hours every Friday — open to all clinics",
    ],
    stat: { value: "< 2 hrs", label: "Average first response from a real human, business hours.", source: "Support SLA" },
    cta: { label: "See support", action: "route", to: "/support" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "We onboard you in person.",
    body: "First 14 days, we're on-site (or on-screen) with your team — listening to calls, dialling in templates, configuring the tone. We don't hand you a Loom and disappear.",
    bullets: [
      "Hands-on configuration, no DIY required",
      "Live listening to your first 100 calls together",
      "Quarterly review built into every contract",
    ],
    stat: { value: "14 days", label: "Hands-on onboarding before we step back.", source: "Onboarding playbook" },
    cta: { label: "See onboarding", action: "demo" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Open architecture. Your data stays yours.",
    body: "We are not a walled garden. APIs are documented, exports are free, the audit log is yours. If you ever leave, you take everything with you — including the AI tone you trained.",
    bullets: [
      "Open APIs · documented · versioned",
      "Free, full data export — JSON / CSV / DB",
      "You own your AI tone profile — exportable",
    ],
    stat: { value: "$0", label: "Off-boarding cost. Your data, free, forever.", source: "Customer charter" },
    cta: { label: "See our charter", action: "route", to: "/security" },
  },
];

/* ───────────────────── SUPPORT ───────────────────── */
export const SUPPORT_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "SLAs published. No surprises.",
    body: "You need answers fast. Our SLAs are on the page, not buried in a contract. Critical issue? Under 30 minutes. Standard ticket? Under 2 hours during business hours.",
    bullets: [
      "P1 critical: < 30 min response, 24/7",
      "P2 standard: < 2 hrs response, business hours",
      "Published SLAs — not stuck behind a sales call",
    ],
    stat: { value: "< 30m", label: "P1 critical response — 24/7. Published, not promised.", source: "Support SLA" },
    cta: { label: "Open a P1", action: "route", to: "/support" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Real humans. Fast answers.",
    body: "No ticket-queue maze. You get a human in your Slack channel during onboarding, and on email after. The same humans who built Clinic OS.",
    bullets: [
      "Slack-channel access for the first 90 days",
      "Email + chat with humans, not bots",
      "Weekly office hours, open to every customer",
    ],
    stat: { value: "0", suffix: "tier-1 bots", label: "We don't put a robot between you and an answer.", source: "Support team" },
    cta: { label: "Email support", action: "route", to: "/support" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "Weekly check-ins until you're flying.",
    body: "For the first 60 days post-launch, we book a weekly 30-min check-in. We review your numbers, we listen to a call together, we tune the AI. Then we hand you the controls.",
    bullets: [
      "60 days of weekly check-ins, built-in",
      "Quarterly business reviews after that — for life",
      "Office hours every Friday — drop in anytime",
    ],
    stat: { value: "60 days", label: "Weekly hand-holding before we step back.", source: "Onboarding playbook" },
    cta: { label: "Talk to onboarding", action: "demo" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Audit log on every ticket.",
    body: "Every interaction, every change, every escalation — captured in the ticket log. Compliance reviewers and finance can audit who touched what, when.",
    bullets: [
      "Full audit trail on every ticket",
      "Field-level change history",
      "Exportable to your GRC / compliance stack",
    ],
    stat: { value: "100%", label: "Of support touches captured in the audit log.", source: "Ticketing system" },
    cta: { label: "See the trust center", action: "route", to: "/security" },
  },
];

/* ───────────────────── DISCIPLINE (generic, used by /disciplines/:slug) ───────────────────── */
export const DISCIPLINE_CHAPTERS = [
  {
    disc: "D",
    eyebrow: "Built to grow",
    title: "Maximize your treatment hours.",
    body: "Your therapists are your revenue engine. Every minute they spend on no-shows, callbacks, or chasing payments is a minute lost. Clinic OS keeps them in treatment rooms.",
    bullets: [
      "No-show recovery on autopilot",
      "Payment reminders + on-file payments",
      "Owner dashboard — therapist utilisation, live",
    ],
    stat: { value: "+18%", label: "Therapist utilisation after the first 90 days.", source: "Pilot clinics" },
    cta: { label: "See the math", action: "demo" },
  },
  {
    disc: "i",
    eyebrow: "Patients first",
    title: "Patients who commit to full programs.",
    body: "One-and-done patients kill your LTV. Reactivation + recall flows nudge patients to finish their program — without your team chasing them on a spreadsheet.",
    bullets: [
      "Reactivation flow for dormant patients",
      "Recall cycles built into the patient record",
      "Patient-facing portal for self-rebooking",
    ],
    stat: { value: "55%", label: "Recall cycle conversion across pilot clinics.", compare: "Industry: ~30%", source: "Reactivation engine" },
    cta: { label: "See reactivation", action: "route", to: "/products/sales-autopilot" },
  },
  {
    disc: "S",
    eyebrow: "Calm by design",
    title: "Calm front desk, even on busy days.",
    body: "Mondays don't melt the desk anymore. AI Receptionist absorbs the call surge, Copilot drafts replies, your front desk focuses on the patients standing in front of them.",
    bullets: [
      "AI absorbs call + comms surges",
      "Front desk freed for face-to-face patients",
      "Sane week even when the schedule is full",
    ],
    stat: { value: "11 hrs", label: "Returned to the front desk every week.", source: "Time-in-tools study" },
    cta: { label: "See KAI", action: "route", to: "/kai" },
  },
  {
    disc: "C",
    eyebrow: "Data first",
    title: "Outcomes data you can show insurers.",
    body: "Pulse rolls up therapist outcomes, recall conversion, and treatment-completion rates by program. Reports you can hand to insurers, partners, or your board.",
    bullets: [
      "Therapist-level outcome dashboards",
      "Program completion + adherence tracking",
      "Audit-grade export for insurers + partners",
    ],
    stat: { value: "100%", label: "Of outcome data traceable to source EMR rows.", source: "Pulse data lineage" },
    cta: { label: "See Clinic Pulse", action: "route", to: "/juvonno/clinicpulse" },
  },
];
